// Evaluate org form calc blocks in document order.

import { round2, safeNumber } from "./calc.js";
import type { OrgFormFilledBlock } from "./orgFormInstanceTypes.js";
import {
  orgFormCalcModeUsesPercentInput,
  parseOrgFormCalcConfig,
  type OrgFormCalcAmountMode,
} from "./orgFormCalcConfig.js";
import { formatOpportunityCurrency } from "./formatCurrency.js";

export type OrgFormBlockRenderItem =
  | { kind: "text"; block: OrgFormFilledBlock }
  | { kind: "calc"; block: OrgFormFilledBlock; amount: number; visible: boolean };

export function evaluateOrgFormCalcAmounts(
  blocks: OrgFormFilledBlock[],
): Map<string, number> {
  const sorted = [...blocks].sort((a, b) => a.sort_order - b.sort_order);
  const amounts = new Map<string, number>();

  for (const block of sorted) {
    if (block.block_type !== "calc") continue;
    const parsed = parseOrgFormCalcConfig(block.calc_config ?? {});
    if ("error" in parsed) {
      amounts.set(block.id, 0);
      continue;
    }

    const inputVal = safeNumber(block.calc_value ?? parsed.default_amount);
    let amount = 0;

    switch (parsed.amount_mode) {
      case "base_price":
      case "fixed":
        amount = inputVal;
        break;
      case "fixed_negative":
        amount = -Math.abs(inputVal);
        break;
      case "percent":
      case "percent_negative": {
        const basis = sumBasisAmounts(parsed.basis_block_ids, amounts);
        const pct = inputVal / 100;
        amount =
          parsed.amount_mode === "percent_negative" ? -round2(basis * pct) : round2(basis * pct);
        break;
      }
      case "total":
        amount = sumPrecedingCalcAmounts(block.id, sorted, amounts);
        break;
      default:
        amount = inputVal;
    }

    amounts.set(block.id, round2(amount));
  }

  return amounts;
}

function sumBasisAmounts(basisIds: string[], amounts: Map<string, number>): number {
  if (basisIds.length === 0) return 0;
  return round2(basisIds.reduce((sum, id) => sum + (amounts.get(id) ?? 0), 0));
}

function sumPrecedingCalcAmounts(
  blockId: string,
  sorted: OrgFormFilledBlock[],
  amounts: Map<string, number>,
): number {
  let sum = 0;
  for (const b of sorted) {
    if (b.id === blockId) break;
    if (b.block_type !== "calc") continue;
    const parsed = parseOrgFormCalcConfig(b.calc_config ?? {});
    if (!("error" in parsed) && parsed.amount_mode === "total") continue;
    sum += amounts.get(b.id) ?? 0;
  }
  return round2(sum);
}

export function buildOrgFormBlockRenderItems(args: {
  blocks: OrgFormFilledBlock[];
  includeCalculations: boolean;
}): OrgFormBlockRenderItem[] {
  const amounts = args.includeCalculations
    ? evaluateOrgFormCalcAmounts(args.blocks)
    : new Map<string, number>();

  const sorted = [...args.blocks].sort((a, b) => a.sort_order - b.sort_order);
  return sorted
    .filter((block) => args.includeCalculations || block.block_type !== "calc")
    .map((block) => {
      if (block.block_type === "calc") {
        const parsed = parseOrgFormCalcConfig(block.calc_config ?? {});
        const visible = "error" in parsed ? true : parsed.visible_on_pdf;
        return {
          kind: "calc" as const,
          block,
          amount: amounts.get(block.id) ?? 0,
          visible,
        };
      }
      return { kind: "text" as const, block };
    });
}

export function formatOrgFormCalcAmount(amount: number): string {
  const abs = formatOpportunityCurrency(Math.abs(amount));
  if (amount < 0) return `(${abs})`;
  return abs;
}

export function orgFormCalcInputSuffix(mode: OrgFormCalcAmountMode): string {
  return orgFormCalcModeUsesPercentInput(mode) ? "%" : "$";
}
